import React, { useState } from 'react';
import { Input } from '../../../foundation/altzero_base/base/components/ui/input';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Label } from '../../../foundation/altzero_base/base/components/ui/label';
import { Switch } from '../../../foundation/altzero_base/base/components/ui/switch';
import { X } from 'lucide-react';

export interface RetailerEmailConfigData {
    emailDomains: string[];
    contactEmails: string[];
    syncEnabled: boolean;
}

interface RetailerEmailConfigProps {
    value: RetailerEmailConfigData;
    onChange: (data: RetailerEmailConfigData) => void;
}

export const RetailerEmailConfig: React.FC<RetailerEmailConfigProps> = ({ value, onChange }) => {
    const [newDomain, setNewDomain] = useState('');
    const [newEmail, setNewEmail] = useState('');

    const updateData = (updates: Partial<RetailerEmailConfigData>) => {
        onChange({ ...value, ...updates });
    };

    const addDomain = (e?: React.MouseEvent | React.KeyboardEvent) => {
        e?.preventDefault();
        if (newDomain.trim() && !value.emailDomains.includes(newDomain.trim())) {
            updateData({ emailDomains: [...value.emailDomains, newDomain.trim()] });
            setNewDomain('');
        }
    };

    const removeDomain = (domain: string) => {
        updateData({ emailDomains: value.emailDomains.filter(d => d !== domain) });
    };

    const addEmail = (e?: React.MouseEvent | React.KeyboardEvent) => {
        e?.preventDefault();
        if (newEmail.trim() && !value.contactEmails.includes(newEmail.trim())) {
            updateData({ contactEmails: [...value.contactEmails, newEmail.trim()] });
            setNewEmail('');
        }
    };

    const removeEmail = (email: string) => {
        updateData({ contactEmails: value.contactEmails.filter(e => e !== email) });
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between pb-2 border-b">
                <Label className="text-base font-semibold">Email Sync Configuration</Label>
                <div className="flex items-center gap-2">
                    <Label htmlFor="sync-enabled" className="text-sm font-normal cursor-pointer">
                        Enable Email Sync
                    </Label>
                    <Switch
                        id="sync-enabled"
                        checked={value.syncEnabled}
                        onCheckedChange={(checked) => updateData({ syncEnabled: checked })}
                    />
                </div>
            </div>

            <div className="space-y-2">
                <Label htmlFor="email-domains">Email Domains</Label>
                <p className="text-xs text-muted-foreground">
                    Add domains to sync emails from (e.g., "agl.com.au")
                </p>
                <div className="flex gap-2">
                    <Input
                        id="email-domains"
                        type="text"
                        value={newDomain}
                        onChange={(e) => setNewDomain(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addDomain(e)}
                        placeholder="e.g., agl.com.au"
                        className="flex-1"
                    />
                    <Button onClick={addDomain} type="button" size="default">
                        Add
                    </Button>
                </div>
                {value.emailDomains.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                        {value.emailDomains.map(domain => (
                            <span
                                key={domain}
                                className="inline-flex items-center gap-1 px-3 py-1 bg-muted border border-border rounded-full text-sm"
                            >
                                @{domain}
                                <button
                                    onClick={() => removeDomain(domain)}
                                    type="button"
                                    className="ml-1 hover:text-destructive transition-colors"
                                    aria-label={`Remove ${domain}`}
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </div>

            <div className="space-y-2">
                <Label htmlFor="contact-emails">Contact Emails</Label>
                <p className="text-xs text-muted-foreground">
                    Add specific email addresses to sync from
                </p>
                <div className="flex gap-2">
                    <Input
                        id="contact-emails"
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addEmail(e)}
                        placeholder="e.g., tenders@agl.com.au"
                        className="flex-1"
                    />
                    <Button onClick={addEmail} type="button" size="default">
                        Add
                    </Button>
                </div>
                {value.contactEmails.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                        {value.contactEmails.map(email => (
                            <span
                                key={email}
                                className="inline-flex items-center gap-1 px-3 py-1 bg-muted border border-border rounded-full text-sm"
                            >
                                {email}
                                <button
                                    onClick={() => removeEmail(email)}
                                    type="button"
                                    className="ml-1 hover:text-destructive transition-colors"
                                    aria-label={`Remove ${email}`}
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
